import React, { Component } from "react";
import { connect } from "react-redux";
import { Alert, Col, Row, Select, Tag, Tooltip, message } from "antd";
import ImportadorExcelGenerico from "../../../../../Components/ImportadorExcelGenerico";
import {
  SelectItem,
  calcularSimilitud,
  dineroDisabledMask,
} from "../../../../../Components/Items";
import ResumenLibros from "./ResumenLibros";
import ResultadosImportacionModal from "./ResultadosImportacionModal";
import {
  normalizarTexto,
  sugerirInsumoParaMaterial,
  construirIndicesAlumnos,
  buscarAlumnoPorMatricula,
} from "./coincidenciaMaterial";
import {
  COLUMNAS_ESPERADAS,
  COLUMNAS_OBLIGATORIAS,
  NOMBRE_PLANTILLA,
  construirPlantilla,
} from "./plantillaVentasLibros";

// Filas por petición. Cada fila crea una venta con póliza, movimiento de inventario y
// correo, así que el archivo completo en una sola petición se cae por timeout.
const TAMANO_LOTE = 20;

const MARCA_CONFIRMADO = "confirmado";
// La revisión se captura a mano y ya trae al menos un "CONFRIMADO" en el archivo real.
// Se tolera la errata sólo en valores de una palabra, para no confundir un
// "ESTA CONFIRMANDO NIVEL" con una confirmación.
const SIMILITUD_CONFIRMADO = 80;

const esConfirmado = (valor) => {
  const norm = normalizarTexto(valor);
  if (!norm) return false;
  if (norm === MARCA_CONFIRMADO) return true;
  if (norm.includes(" ")) return false;
  return calcularSimilitud(norm, MARCA_CONFIRMADO) >= SIMILITUD_CONFIRMADO;
};

const instrucciones = [
  <>
    Sube <b>tal cual</b> el Excel de respuestas del formulario. No hay que reacomodar
    columnas: se reconocen por su encabezado (revisa la hoja <b>Columnas</b> de la
    plantilla si algo no se detecta). Si no hay formulario, captura a mano en la hoja{" "}
    <b>Respuestas</b> de la plantilla.
  </>,
  <>
    <b>Los libros no se eligen aquí:</b> salen de la <b>Lista Escolar</b> del alumno (la
    suya o la de su grupo). De esa lista se toman <b>todos</b> los insumos marcados como{" "}
    <b>Libro Virtual</b>: si la lista trae dos, la venta lleva los dos renglones y cada uno
    recibe su propio código. Lo que el alumno escribió en el formulario sólo se usa para
    avisarte si no está en su lista.
  </>,
  <>
    Elige <b>Proyecto</b>, <b>Banco</b> y <b>Forma de Pago</b>. La venta se registra como{" "}
    <b>remisión de contado</b>: genera su póliza de ingreso y el cobro al banco elegido.
  </>,
  <>
    Sólo se procesan las filas que dicen <b>CONFIRMADO</b> en la columna de revisión. Las
    demás aparecen como <b>Omitir</b> para que las veas, pero no generan venta.
  </>,
  <>
    Si el alumno <b>ya tiene una venta</b> (cualquiera, no cancelada), también se omite y
    se indica el folio que ya tiene. Esto se vuelve a verificar al momento de importar.
  </>,
  <>
    Al importar se crea la venta, se le asigna un <b>código del libro</b> descontándolo del
    inventario y se manda el <b>correo</b> con el código. Va al correo que el alumno capturó
    en el formulario <b>y</b> a los que ya tenga dados de alta en su ficha; si el del
    formulario es nuevo, se le guarda al alumno.
  </>,
  <>
    El proceso va por bloques y puede tardar varios minutos. <b>No cierres la pestaña.</b>{" "}
    Al terminar se muestra el detalle fila por fila y se puede descargar en Excel.
  </>,
];

/**
 * Clasifica una fila del archivo. Se usa dos veces: en la pasada previa que arma el
 * resumen de libros, y por cada fila desde el importador. Por eso recibe todo lo que
 * necesita en vez de leer del estado.
 */
const clasificarFila = ({
  fila,
  indice,
  columnas,
  indices,
  folioPorCliente,
  clavesUsadas,
  insumosLibro,
}) => {
  const errores = [];

  const numeroFila = (fila["__rowNum__"] ?? indice) + 1;
  const matricula = String(fila[columnas.matricula] ?? "").trim();
  const material = String(fila[columnas.material] ?? "").trim();
  const correoExcel = String(fila[columnas.correo] ?? "").trim();
  const revision = String(fila[columnas.revision] ?? "").trim();

  const nombreExcel = [
    fila[columnas.nombre],
    fila[columnas.apellidoPaterno],
    fila[columnas.apellidoMaterno],
  ]
    .map((parte) => String(parte ?? "").trim())
    .filter(Boolean)
    .join(" ");

  if (!matricula) {
    errores.push({ campo: "Número de control", motivo: "está vacío" });
  }

  const { alumno, criterio } = matricula
    ? buscarAlumnoPorMatricula(matricula, correoExcel, indices)
    : { alumno: null, criterio: "noEncontrado" };

  if (matricula && !alumno) {
    errores.push({
      campo: "Número de control",
      motivo:
        criterio === "ambiguo"
          ? `"${matricula}" coincide con más de un alumno; corrige la matrícula en el archivo`
          : `"${matricula}" no corresponde a ningún alumno registrado`,
    });
  }

  if (errores.length > 0) {
    return { ok: false, errores };
  }

  const confirmado = esConfirmado(revision);

  // Una lista puede traer varios Libros Virtuales: la venta lleva un renglón por cada uno.
  // Los libros y su precio ya vienen resueltos del backend, con la misma función que usa la
  // importación, para que el preview no pueda discrepar de lo que realmente se va a cobrar.
  const librosAlumno = (alumno.libros || []).map((valueLibro) => {
    const insumo = (insumosLibro || []).find(
      (valueInsumo) => String(valueInsumo._id) === String(valueLibro.insumoId),
    );
    return {
      insumoId: String(valueLibro.insumoId),
      cantidad: valueLibro.cantidad || 1,
      precioUnitario: valueLibro.precioUnitario,
      descripcion: insumo?.descripcion || "(insumo no encontrado)",
      sinCodigoSat: insumo ? !insumo.codigoSatId : false,
    };
  });

  // El texto del formulario no manda, pero si apunta claramente a un libro que NO está en
  // su lista vale la pena avisarlo: casi siempre significa que quedó en el grupo equivocado.
  let materialCoincide = null;
  if (material && librosAlumno.length > 0) {
    const sugerido = sugerirInsumoParaMaterial(material, insumosLibro || []);
    if (sugerido) {
      materialCoincide = librosAlumno.some(
        (valueLibro) => String(valueLibro.insumoId) === String(sugerido._id),
      );
    }
  }

  // El código se manda al correo del formulario Y a los que ya tiene el alumno en su ficha.
  // El del formulario va primero y, si es nuevo, se le guarda al cliente al importar.
  const destinatarios = [];
  [correoExcel, alumno.correo, ...(alumno.correosAdicionales || [])].forEach(
    (valueCorreo) => {
      const correo = String(valueCorreo || "").trim();
      if (!correo) return;
      if (destinatarios.some((c) => c.toLowerCase() === correo.toLowerCase())) return;
      destinatarios.push(correo);
    },
  );

  const datos = {
    numeroFila,
    matricula,
    material,
    correo: destinatarios.join(", "),
    correoNuevo:
      !!correoExcel &&
      ![alumno.correo, ...(alumno.correosAdicionales || [])].some(
        (valueCorreo) =>
          String(valueCorreo || "").trim().toLowerCase() === correoExcel.toLowerCase(),
      ),
    clienteId: String(alumno._id),
    alumno:
      [alumno.nombre, alumno.apellidoPaterno, alumno.apellidoMaterno]
        .filter(Boolean)
        .join(" ")
        .trim() || nombreExcel,
    listaNombre: alumno.listaNombre || "",
    libros: librosAlumno,
    cantidad: librosAlumno.reduce((acc, valueLibro) => acc + valueLibro.cantidad, 0),
    total: alumno.total,
    listaPrecioNombre: alumno.listaPrecioNombre || "",
    materialCoincide,
  };

  if (!confirmado) {
    return {
      ok: true,
      accion: "sinCambios",
      datos: {
        ...datos,
        motivo: revision ? `Revisión dice "${revision}", no CONFIRMADO` : "Sin confirmar",
      },
    };
  }

  const folio = folioPorCliente.get(String(alumno._id));
  if (folio !== undefined) {
    return {
      ok: true,
      accion: "sinCambios",
      datos: {
        ...datos,
        motivo: `El alumno ya tiene la venta ${folio || "(sin folio)"}`,
      },
    };
  }

  // Un alumno con dos filas confirmadas sólo puede recibir una venta. El backend igual lo
  // atraparía (procesa en serie y vuelve a consultar), pero así se ve desde el preview
  // cuál de las dos se va a quedar fuera.
  const llaveAlumno = "cliente:" + String(alumno._id);
  if (clavesUsadas.has(llaveAlumno)) {
    return {
      ok: true,
      accion: "sinCambios",
      datos: {
        ...datos,
        motivo: `Alumno repetido en el archivo (ya viene en la fila ${clavesUsadas.get(llaveAlumno)})`,
      },
    };
  }
  // El problema de lista o de precio se reporta hasta aquí, cuando ya se sabe que esa fila
  // SÍ iba a generar venta: un alumno omitido por no estar confirmado o por ya tener venta
  // no necesita que le arreglen la lista.
  //
  // Va como accion "error" y no como `ok: false` para que la fila se VEA en la tabla con el
  // alumno y el motivo: son problemas de configuración del catálogo, y esconderlos en
  // errores.xlsx obliga a descargar un archivo para saber a quién hay que arreglarle qué.
  if (alumno.libroError) {
    return {
      ok: true,
      accion: "error",
      datos: { ...datos, motivo: alumno.libroError },
    };
  }

  clavesUsadas.set(llaveAlumno, numeroFila);

  return {
    ok: true,
    accion: "crear",
    datos: { ...datos, motivo: alumno.libroAviso || "" },
  };
};

class ImportarVentasLibros extends Component {
  state = {
    catalogos: null,
    libros: [],
    proyectoId: undefined,
    bancoId: undefined,
    formaDePagoId: undefined,
    modalVisible: false,
    resultado: null,
  };

  // El importador guarda los catálogos en su propio estado; este hook es el único punto
  // donde también llegan aquí, que es donde se necesitan para los selects y el resumen.
  prepararCatalogos = (resData) => {
    this.setState({ catalogos: resData });
    return resData;
  };

  /**
   * Resuelve los encabezados reales del archivo, arma los índices de búsqueda y hace una
   * pasada previa para saber qué libros y cuántos códigos va a necesitar la importación.
   * Corre una sola vez por archivo, antes de validar las filas.
   */
  prepararContextoArchivo = (hojas, catalogos) => {
    const filas = hojas?.[0]?.data || [];

    const llaves = new Set();
    filas.forEach((fila) => {
      Object.keys(fila).forEach((llave) => {
        if (!llave.startsWith("__")) llaves.add(llave);
      });
    });

    const columnas = {};
    COLUMNAS_ESPERADAS.forEach((esperada) => {
      const encontrada = [...llaves].find((llave) =>
        normalizarTexto(llave).startsWith(esperada.prefijo),
      );
      if (encontrada) columnas[esperada.clave] = encontrada;
    });

    const faltantes = COLUMNAS_OBLIGATORIAS.filter((clave) => !columnas[clave]);
    if (faltantes.length > 0) {
      const etiquetas = COLUMNAS_ESPERADAS.filter((c) => faltantes.includes(c.clave))
        .map((c) => c.etiqueta)
        .join(", ");
      message.error(`No se encontraron las columnas: ${etiquetas}`);
    }

    const indices = construirIndicesAlumnos(catalogos?.alumnos);
    const folioPorCliente = new Map(
      (catalogos?.clientesConVenta || []).map((valueVenta) => [
        String(valueVenta.clienteId),
        valueVenta.folio,
      ]),
    );
    const insumosLibro = catalogos?.insumosLibro || [];

    // Pasada previa: mismo criterio que la validación fila por fila, con su propio mapa de
    // repetidos para no contaminar el del importador.
    const clavesPrevias = new Map();
    const porInsumo = new Map();
    filas.forEach((fila, indice) => {
      const resultado = clasificarFila({
        fila,
        indice,
        columnas,
        indices,
        folioPorCliente,
        clavesUsadas: clavesPrevias,
        insumosLibro,
      });
      if (!resultado.ok || resultado.accion !== "crear") return;
      // Un alumno puede llevar varios libros: cada uno suma a su propio renglón del resumen.
      resultado.datos.libros.forEach((valueLibro) => {
        const acumulado = porInsumo.get(valueLibro.insumoId) || {
          insumoId: valueLibro.insumoId,
          cantidad: 0,
          alumnos: 0,
        };
        acumulado.cantidad += valueLibro.cantidad;
        acumulado.alumnos += 1;
        porInsumo.set(valueLibro.insumoId, acumulado);
      });
    });

    this.setState({
      libros: [...porInsumo.values()].sort((a, b) => b.alumnos - a.alumnos),
    });

    return { columnas, indices, folioPorCliente, insumosLibro };
  };

  validarFila = (fila, catalogos, indice, contexto) =>
    clasificarFila({
      fila,
      indice,
      columnas: contexto.columnas,
      indices: contexto.indices,
      folioPorCliente: contexto.folioPorCliente,
      clavesUsadas: contexto.clavesYaUsadas,
      insumosLibro: contexto.insumosLibro,
    });

  columnasPreview = () => [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 120 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 200 },
    { title: "Lista", dataIndex: "listaNombre", key: "listaNombre", width: 160 },
    {
      title: "Libros (de su lista)",
      key: "libro",
      width: 280,
      render: (_, record) => {
        if (!record.libros || record.libros.length === 0) {
          return <Tag color="red">Sin libro</Tag>;
        }
        return (
          <>
            {record.libros.map((valueLibro) => (
              <div key={valueLibro.insumoId}>
                {valueLibro.cantidad > 1 ? `${valueLibro.cantidad} x ` : ""}
                {valueLibro.descripcion}
                {valueLibro.sinCodigoSat && (
                  <Tag color="red" style={{ marginLeft: 6 }}>
                    Sin código SAT
                  </Tag>
                )}
              </div>
            ))}
            {record.materialCoincide === false && (
              <Tooltip
                title={`En el formulario pidió "${record.material}", que no está en su lista. Revisa su grupo o su lista.`}
              >
                <Tag color="orange">No coincide</Tag>
              </Tooltip>
            )}
          </>
        );
      },
    },
    { title: "Cant.", dataIndex: "cantidad", key: "cantidad", width: 70, align: "center" },
    {
      title: "Total",
      key: "precio",
      width: 130,
      align: "right",
      render: (_, record) => {
        if (record.total === null || record.total === undefined) {
          return <Tag color="orange">Sin precio</Tag>;
        }
        return (
          <Tooltip title={`Lista de precios: ${record.listaPrecioNombre || "—"}`}>
            {dineroDisabledMask(record.total)}
          </Tooltip>
        );
      },
    },
    { title: "Pidió en el formulario", dataIndex: "material", key: "material", width: 220 },
    {
      title: "Se envía a",
      key: "correo",
      width: 240,
      render: (_, record) => (
        <>
          {record.correo || <Tag color="orange">Sin correo</Tag>}
          {record.correoNuevo && (
            <Tooltip title="El correo del formulario no está en la ficha del alumno; se le va a guardar al importar.">
              <Tag color="blue" style={{ marginLeft: 6 }}>
                Correo nuevo
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 260 },
  ];

  antesDeImportar = () => {
    const { proyectoId, bancoId, formaDePagoId, libros, catalogos } = this.state;

    if (!proyectoId || !bancoId || !formaDePagoId) {
      message.warning("Selecciona Proyecto, Banco y Forma de Pago antes de importar");
      return false;
    }

    // agregarVenta rechaza la venta completa si el insumo no trae código del SAT: sin este
    // candado se caerían todas las filas de ese libro, una por una.
    const sinCodigoSat = libros
      .map((libro) =>
        (catalogos?.insumosLibro || []).find(
          (insumo) => String(insumo._id) === String(libro.insumoId),
        ),
      )
      .filter((insumo) => insumo && !insumo.codigoSatId);

    if (sinCodigoSat.length > 0) {
      message.warning(
        `Estos libros no tienen código del SAT y no se pueden vender: ${sinCodigoSat
          .map((insumo) => insumo.descripcion)
          .join(", ")}`,
      );
      return false;
    }

    return true;
  };

  prepararPayloadExtra = () => ({
    proyectoId: this.state.proyectoId,
    bancoId: this.state.bancoId,
    formaDePagoId: this.state.formaDePagoId,
  });

  onImportSuccess = (resData) => {
    this.setState({ resultado: resData, modalVisible: true });
    return true;
  };

  cerrarModal = () => {
    this.setState({ modalVisible: false, resultado: null });
    if (this.props.location?.state?.volverA) {
      this.props.history.replace(this.props.location.state.volverA);
    } else {
      this.props.history.goBack();
    }
  };

  controlesExtra = () => {
    const { catalogos, proyectoId, bancoId, formaDePagoId } = this.state;

    const bancos = catalogos?.bancos || [];
    const monedas = catalogos?.monedas || [];
    const bancoElegido = bancos.find((banco) => String(banco._id) === String(bancoId));
    const monedaBanco = monedas.find(
      (moneda) => String(moneda._id) === String(bancoElegido?.monedaId),
    );

    return (
      <>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Cómo se registra cada venta"
          description="Remisión de contado con un renglón por cada Libro Virtual de la Lista Escolar del alumno, al precio de la lista de precios que hereda. Genera la póliza de ingreso y el cobro al banco seleccionado, entrega los códigos descontándolos del inventario y manda el correo al alumno con todos ellos."
        />

        <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Proyecto</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Proyecto"
              value={proyectoId}
              onChange={(valor) => this.setState({ proyectoId: valor })}
            >
              {(catalogos?.proyectos || []).map((proyecto) => (
                <Select.Option value={String(proyecto._id)} key={String(proyecto._id)}>
                  {proyecto.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Banco {monedaBanco ? `(${monedaBanco.abreviacion})` : ""}
            </div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Banco"
              value={bancoId}
              onChange={(valor) => this.setState({ bancoId: valor })}
            >
              {bancos.map((banco) => (
                <Select.Option value={String(banco._id)} key={String(banco._id)}>
                  {banco.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Forma de Pago</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Forma de Pago"
              value={formaDePagoId}
              onChange={(valor) => this.setState({ formaDePagoId: valor })}
            >
              {(catalogos?.formasDePago || []).map((forma) => (
                <Select.Option value={String(forma._id)} key={String(forma._id)}>
                  {forma.c_FormaPago + " - " + forma.descripcion}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
        </Row>

        <ResumenLibros
          libros={this.state.libros}
          insumos={catalogos?.insumosLibro || []}
          disponiblesPorInsumo={catalogos?.disponiblesPorInsumo || []}
          proyectoId={proyectoId}
        />
      </>
    );
  };

  render() {
    return (
      <>
        <ImportadorExcelGenerico
          {...this.props}
          titulo="Importar Ventas de Libros"
          nombreEntidadPlural="ventas"
          endpointDatosImportar="ventasLibrosEscuela/datosParaImportarVentasLibros"
          endpointImportar="ventasLibrosEscuela/importarVentasLibros"
          nombrePlantilla={NOMBRE_PLANTILLA}
          construirPlantilla={construirPlantilla}
          prepararCatalogos={this.prepararCatalogos}
          prepararContextoArchivo={this.prepararContextoArchivo}
          validarFila={this.validarFila}
          columnasPreview={this.columnasPreview()}
          instrucciones={instrucciones}
          controlesExtra={this.controlesExtra()}
          antesDeImportar={this.antesDeImportar}
          prepararPayloadExtra={this.prepararPayloadExtra}
          onImportSuccess={this.onImportSuccess}
          tamanoLote={TAMANO_LOTE}
          etiquetasAccion={{
            crear: "Crear venta",
            editar: null,
            sinCambios: "Omitir",
            error: "Revisar",
          }}
          permitirEdicion
        />

        <ResultadosImportacionModal
          visible={this.state.modalVisible}
          resultado={this.state.resultado}
          cerrarModal={this.cerrarModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarVentasLibros);
